/**
 * Hook do overview de PLD (Task #190 Fase 2 — consome /api/v2/pld/overview).
 *
 * Mesmo padrão do useDreV2: cache module-level KEYED por (mes_ref, submercado),
 * refetch ao trocar params, reset de loading pra não mostrar dado stale. TTL
 * 5 min — PLD muda no máximo 1x/dia (cron CCEE / import manual).
 *
 * `usePldOverviewAll` chama o hook 4× (SE/S/NE/N) pra alimentar a sub-tab
 * "Comparação Submercados" — o backend é por submercado, então a combinação
 * das 4 séries é client-side (sem mexer no backend).
 */
import { useEffect, useState } from "react";
import { getPldOverview } from "../api/client";
import type { PldOverviewResponse, PldSubmercado } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldOverviewResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldOverviewCache(): void {
  cache.clear();
}

export interface UsePldOverviewState {
  data: PldOverviewResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldOverview(
  mesRef: string,
  submercado: string,
): UsePldOverviewState {
  const [state, setState] = useState<UsePldOverviewState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${mesRef}|${submercado}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldOverview(mesRef, submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mesRef, submercado]);

  return state;
}

export interface UsePldOverviewAllState {
  bySubmercado: Record<PldSubmercado, PldOverviewResponse | null>;
  isLoading: boolean;
  error: Error | null;
}

/** Overview das 4 submercados (SE/S/NE/N) pro mês — para a sub-tab Comparação.
 * Chama usePldOverview 4× (contagem fixa, ok com as regras de hooks). */
export function usePldOverviewAll(mesRef: string): UsePldOverviewAllState {
  const se = usePldOverview(mesRef, "SE");
  const s = usePldOverview(mesRef, "S");
  const ne = usePldOverview(mesRef, "NE");
  const n = usePldOverview(mesRef, "N");
  return {
    bySubmercado: { SE: se.data, S: s.data, NE: ne.data, N: n.data },
    isLoading: se.isLoading || s.isLoading || ne.isLoading || n.isLoading,
    error: se.error ?? s.error ?? ne.error ?? n.error,
  };
}
